import moment from 'moment-timezone'
import { RENEWAL_TZ } from './renewal'

/**
 * Flow A of the add-on renewal (spec docs/addon-renewal-spec.md, sections 3
 * and 7a): what the primary's add-on rows show, and when their Renew button
 * is enabled.
 */

// PrimaryRenewalService.createLiveRenewal (dashboards-api) tags an expired
// primary's live renewal subscription with this metadata. The sender
// (AddOnRenewalEmailService.primaryRenewal) finds the live renewal the same way.
export const LIVE_RENEWAL_METADATA_KEY = 'twRenewalMode'
export const LIVE_RENEWAL_METADATA_VALUE = 'live'

export const RENEW_FIRST_MESSAGE = 'Renew your membership first'

/** An active subscription that is a live (expired-primary) renewal. */
export const hasLiveRenewal = (subscriptions = []) => (subscriptions || []).some((sub) =>
    sub && sub.status === 'active' &&
    sub.metadata && sub.metadata[LIVE_RENEWAL_METADATA_KEY] === LIVE_RENEWAL_METADATA_VALUE
)

/**
 * When the primary's current renewal term starts, as a 'YYYY-MM-DD' (UTC) key
 * comparable with the start half of an add-on renewal's `term`
 * (AddOnRenewalService.termKey). The sender picks the same renewal: a
 * not-started schedule first, else the live renewal subscription. Null when
 * neither is known (e.g. a live renewal made in this session).
 */
export const primaryRenewalStartKey = ({ membershipScheduled = [], subscriptions = [] }) => {
    const starts = (membershipScheduled || [])
        .map((s) => s && s.phases && s.phases[0] && s.phases[0].start_date)
        .filter(Boolean)
        .sort((a, b) => a - b)
    let start = starts[0]
    if (!start) {
        const live = (subscriptions || []).find((sub) =>
            sub && sub.status === 'active' &&
            sub.metadata && sub.metadata[LIVE_RENEWAL_METADATA_KEY] === LIVE_RENEWAL_METADATA_VALUE)
        start = live && live.start_date
    }
    return start ? moment.utc(Number(start) * 1000).format('YYYY-MM-DD') : null
}

const termStartKey = (renewal) =>
    renewal && typeof renewal.term === 'string' ? renewal.term.split('_')[0] : null

/**
 * Has the primary renewed? The same test the server applies before it sends
 * (409 "Renew your membership first" otherwise):
 *   - a renewal schedule exists (membershipScheduled from getMembershipPhases), or
 *   - a live renewal exists: set in this session (liveRenewedUntil), or an
 *     active subscription carrying the live-renewal metadata after a reload.
 */
export const primaryHasRenewed = ({ membershipScheduled = [], liveRenewedUntil = null, subscriptions = [] }) =>
    (membershipScheduled || []).length > 0 || !!liveRenewedUntil || hasLiveRenewal(subscriptions)

const toMoment = (value) => {
    if (value === null || value === undefined || value === '') return null
    const m = typeof value === 'number' || /^\d+$/.test(String(value))
        ? moment.tz(Number(value) * 1000, RENEWAL_TZ)
        : moment.tz(value, RENEWAL_TZ)
    return m.isValid() ? m : null
}

export const formatBillingEndsAt = (value) => {
    const m = toMoment(value)
    return m ? m.format('MMM D, YYYY') : null
}

/** Removed (spec 7a): a cancelled renewal that still has billing to run out. */
const isRemovedWithAccess = (renewal) =>
    !!renewal && renewal.status === 'cancelled' && !!renewal.billingEndsAt

export const RENEWAL_STATUS_LINES = {
    emailed: 'Renewal email sent',
    in_progress: 'In progress',
    signed: 'Signed — enrolling'
}

/**
 * What one add-on row shows for its renewal.
 *
 *   { action: 'renew' | 'resend' | null, statusLine: string | null }
 *
 * renewal null / expired / cancelled  -> Renew
 * emailed / in_progress / signed      -> Resend, with a status line
 * enrolled                            -> "Renewed", no button
 * cancelled with billingEndsAt        -> "Removed, access until {date}", no button
 *
 * Only accepted rows can renew; any other row gets no action (but a removed
 * row still shows its access end date).
 */
export const addOnRenewalState = (member, { primaryRenewalStart = null } = {}) => {
    let renewal = member && member.renewal

    // A renewal for an earlier term than the primary's current renewal (e.g.
    // last year's, still 'enrolled') says nothing about this term: renew again.
    const renewalStart = termStartKey(renewal)
    if (primaryRenewalStart && renewalStart && renewalStart < primaryRenewalStart && !isRemovedWithAccess(renewal)) {
        renewal = null
    }

    if (isRemovedWithAccess(renewal)) {
        const until = formatBillingEndsAt(renewal.billingEndsAt)
        return { action: null, statusLine: until ? `Removed, access until ${until}` : 'Removed' }
    }
    if (!member || member.status !== 'accepted') {
        return { action: null, statusLine: null }
    }

    const status = renewal && renewal.status
    if (status === 'enrolled') {
        return { action: null, statusLine: 'Renewed' }
    }
    if (RENEWAL_STATUS_LINES[status]) {
        return { action: 'resend', statusLine: RENEWAL_STATUS_LINES[status] }
    }
    return { action: 'renew', statusLine: null }
}

/**
 * The rows the account page lists. Invited and accepted rows always; for the
 * primary, also a removed row whose access has not run out yet (spec 7a), so
 * "Removed, access until {date}" can be shown on it. Removed rows never count
 * towards the add-on slots - callers count invited/accepted rows for that.
 */
export const addOnsToList = (addOns = [], { isPrimary = false, now = new Date() } = {}) =>
    (addOns || []).filter((member) => {
        if (member.status === 'invited' || member.status === 'accepted') return true
        if (!isPrimary || !isRemovedWithAccess(member.renewal)) return false
        const until = toMoment(member.renewal.billingEndsAt)
        return !!until && !until.isBefore(moment.tz(now, RENEWAL_TZ), 'day')
    })

const sameEmail = (a, b) => !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase()

/**
 * The renewal an add-on viewer still has to complete on their own account
 * page ("Complete your renewal"), from the memberFamily response. The email
 * link goes straight to /addon-renewal/:renewalId; this is the way back in
 * after an ordinary login.
 *
 * Only for a viewer who is not the primary, on their own accepted row, with a
 * renewal that is emailed, in_progress or signed. Null otherwise: a primary,
 * no row, enrolled / expired / cancelled, or no renewal yet.
 */
export const ownPendingRenewal = (family) => {
    const self = family && family.self
    if (!self || self.isPrimary) return null
    const row = (family.addOns || []).find((addOn) =>
        addOn && addOn.member && (
            (self.recordId && addOn.member.recordId === self.recordId) ||
            sameEmail(addOn.member.email, self.email)))
    if (!row || row.status !== 'accepted') return null
    const renewal = row.renewal
    if (!renewal || !renewal.renewalId || !RENEWAL_STATUS_LINES[renewal.status]) return null
    return { renewalId: renewal.renewalId, status: renewal.status }
}

/**
 * The calling add-on's own latest renewal, whatever its status, for the term
 * card: { status, termStart, termEnd } or null.
 */
export const ownRenewalTerm = (family) => {
    const self = family && family.self
    if (!self || self.isPrimary) return null
    const row = (family.addOns || []).find((addOn) =>
        addOn && addOn.member && (
            (self.recordId && addOn.member.recordId === self.recordId) ||
            sameEmail(addOn.member.email, self.email)))
    const renewal = row && row.renewal
    if (!renewal || !renewal.status) return null
    return { status: renewal.status, termStart: renewal.termStart || null, termEnd: renewal.termEnd || null }
}

/**
 * What an add-on's own page shows for Current Status, Standing and Membership
 * expires on when the primary pays for them: the primary's membership
 * (memberFamily primary.membership). Display only - the add-on's renew window
 * is never computed from it.
 *
 * Null unless the viewer is an add-on (not the primary), has no subscription
 * of their own, and the primary's membership was read.
 *
 *   { status: 'active', delinquent: false, expiresOn: ['Nov 15 2026'], primaryName }
 */
export const coveredMembershipDisplay = (family, ownSubscriptions = []) => {
    const self = family && family.self
    const primary = family && family.primary
    const membership = primary && primary.membership
    if (!self || self.isPrimary || !membership) return null
    if ((ownSubscriptions || []).length > 0) return null
    const subs = membership.subscriptions || []
    const name = [primary.firstName, primary.lastName].filter(Boolean).join(' ') || null
    return {
        status: subs.length ? String(subs[0].status || '').replace('_', ' ') : null,
        delinquent: !!membership.delinquent,
        expiresOn: subs.map((sub) => sub.cancelAt
            ? moment.tz(sub.cancelAt * 1000, RENEWAL_TZ).format('MMM D, YYYY')
            : 'Ongoing'),
        primaryName: name
    }
}
